import moment from 'moment';
import { useMemo } from 'react';
import { ArrowsDownUpIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  usePagination,
  PaginationEllipsis,
} from '../../components/ui/pagination';

export default function MistakesTable({
  title,
  sortBy,
  onSortByChange,
  data,
  showDate,
  onRowClick,
  nameColumnTitle,
  currentPage,
  totalPages,
  onPageChange,
}) {
  const sortOptions = [
    { value: 'name-asc', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
    { value: 'attempted-wrong-hl', label: 'Attempted Wrong (H-L)' },
    { value: 'attempted-wrong-lh', label: 'Attempted Wrong (L-H)' },
    { value: 'not-attempted-hl', label: 'Not Attempted (H-L)' },
    { value: 'not-attempted-lh', label: 'Not Attempted (L-H)' },
    { value: 'attempted-correct-hl', label: 'Attempted Correct (H-L)' },
    { value: 'attempted-correct-lh', label: 'Attempted Correct (L-H)' },
  ];

  const sortedData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    let dataToSort = [...data];

    const nameKey = dataToSort[0]?.chapterTitle ? 'chapterTitle' : 'testTitle';

    switch (sortBy) {
      case 'name-asc':
        dataToSort.sort((a, b) => (a[nameKey] || '').localeCompare(b[nameKey] || ''));
        break;
      case 'name-desc':
        dataToSort.sort((a, b) => (b[nameKey] || '').localeCompare(a[nameKey] || ''));
        break;
      case 'attempted-wrong-hl':
        dataToSort.sort((a, b) => b.totalIncorrect - a.totalIncorrect);
        break;
      case 'attempted-wrong-lh':
        dataToSort.sort((a, b) => a.totalIncorrect - b.totalIncorrect);
        break;
      case 'not-attempted-hl':
        dataToSort.sort((a, b) => b.totalUnattempted - a.totalUnattempted);
        break;
      case 'not-attempted-lh':
        dataToSort.sort((a, b) => a.totalUnattempted - b.totalUnattempted);
        break;
      case 'attempted-correct-hl':
        dataToSort.sort((a, b) => b.totalCorrect - a.totalCorrect);
        break;
      case 'attempted-correct-lh':
        dataToSort.sort((a, b) => a.totalCorrect - b.totalCorrect);
        break;
      default:
        // When sortBy is 'default', return original data without sorting
        return data;
    }
    return dataToSort;
  }, [data, sortBy]);

  const selectValue = sortBy && sortOptions.some((option) => option.value === sortBy) ? sortBy : undefined;

  const paginationRange = usePagination({
    totalPages: totalPages,
    currentPage: currentPage,
  });

  const handlePageChange = (page) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  return (
    <Card className="py-0 gap-0 overflow-hidden mb-15 lg:mb-0">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-lg text-[var(--q3-neutral-default)]">{title}</CardTitle>

        {/* <Select value={selectValue} onValueChange={onSortByChange}>
          <SelectTrigger className="w-auto rounded-full">
            <ArrowsDownUpIcon className="h-4 w-4 mr-1" />
            <SelectValue placeholder="Sort By" />
          </SelectTrigger>
          <SelectContent>
            {sortOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select> */}
      </CardHeader>
      <hr />
      <CardContent className="px-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--q3-surface-dim)]">
              <tr className="h-18 align-middle border-b border-[var(--q3-stroke-normal)]">
                <th
                  className="text-left text-sm py-3 px-6 font-medium text-[var(--q3-neutral-default)] border-r border-[var(--q3-stroke-normal)]"
                  style={{ minWidth: '250px' }}
                >
                  {nameColumnTitle}
                </th>
                <th
                  className="text-center text-sm py-3 px-4 font-medium text-[var(--q3-neutral-default)] border-r border-[var(--q3-stroke-normal)]"
                  style={{ minWidth: '100px' }}
                >
                  Accuracy
                  <br />
                  Trend
                </th>
                <th
                  className="text-center text-sm py-3 px-4 font-medium text-[var(--q3-danger-normal)] border-r border-[var(--q3-stroke-normal)]"
                  style={{ minWidth: '120px' }}
                >
                  Attempted
                  <br />
                  Wrong
                </th>
                <th
                  className="text-center text-sm py-3 px-4 font-medium text-[var(--q3-neutral-light)] border-r border-[var(--q3-stroke-normal)]"
                  style={{ minWidth: '120px' }}
                >
                  Not
                  <br />
                  Attempted
                </th>
                <th
                  className="text-center text-sm py-3 px-4 font-medium text-[var(--q3-success-normal)] border-r border-[var(--q3-stroke-normal)]"
                  style={{ minWidth: '120px' }}
                >
                  Attempted
                  <br />
                  Correct
                </th>
                <th
                  className="text-center text-sm py-3 px-4 font-medium text-[var(--q3-neutral-default)]"
                  style={{ minWidth: '120px' }}
                >
                  Total
                  <br />
                  Questions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.length > 0 ? (
                sortedData.map((item, index) => {
                  const accuracy =
                    item.totalQuestions > 0 ? ((item.totalCorrect / item.totalAttempted) * 100).toFixed(2) : 0;
                  const incorrectPercentage =
                    item.totalQuestions > 0 ? ((item.totalIncorrect / item.totalAttempted) * 100).toFixed(2) : 0;
                  const itemName = item.chapterTitle || item.testTitle;
                  const itemIcon = item.chapterIcon;

                  return (
                    <tr
                      key={item._id || item.testId || index}
                      className="h-18 align-middle border-b border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dim)] cursor-pointer"
                      onClick={() => onRowClick(item)}
                    >
                      <td className="py-3 px-6 text-sm border-r border-[var(--q3-stroke-normal)]">
                        <div className="flex items-center gap-2">
                          {itemIcon && <img src={itemIcon || '/placeholder.svg'} alt={itemName} className="h-4 w-4" />}
                          <div className="flex justify-between items-center w-full">
                            <span className="flex flex-col text-[var(--q3-neutral-default)] max-w-[240px]">
                              <span className="truncate text-ellipsis whitespace-nowrap overflow-hidden">
                                {itemName}
                              </span>
                              {showDate && (
                                <span className="text-xs text-[var(--q3-neutral-light)]">
                                  {moment(item?.submittedAt).format('D MMM YYYY')}
                                </span>
                              )}
                            </span>
                            <CaretRightIcon size={16} className="text-[var(--q3-neutral-light)]" />
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-6 text-sm text-center border-r border-[var(--q3-stroke-normal)]">
                        <div className="flex items-center justify-center">
                          <div className="relative w-12 h-12">
                            <svg className="w-10 h-10 transform -rotate-90" viewBox="0 0 36 36">
                              {/* Background track */}
                              <circle
                                cx="18"
                                cy="18"
                                r="15.9155"
                                fill="none"
                                stroke="var(--q3-neutral-light)"
                                strokeWidth="3"
                              />

                              {/* Accuracy Segment */}
                              {accuracy > 0 && (
                                <circle
                                  cx="18"
                                  cy="18"
                                  r="15.9155"
                                  fill="none"
                                  stroke="var(--q3-success-normal)"
                                  strokeWidth="3"
                                  strokeDasharray={`${accuracy} ${100 - accuracy}`}
                                />
                              )}

                              {/* Incorrect segment placed after accuracy */}
                              {incorrectPercentage > 0 && (
                                <circle
                                  cx="18"
                                  cy="18"
                                  r="15.9155"
                                  fill="none"
                                  stroke="var(--q3-danger-normal)"
                                  strokeWidth="3"
                                  strokeDasharray={`${incorrectPercentage} ${100}`}
                                  strokeDashoffset={`-${accuracy}`}
                                />
                              )}
                            </svg>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-6 text-sm text-center border-r border-[var(--q3-stroke-normal)]">
                        <span className="text-[var(--q3-danger-normal)] font-bold">{item.totalIncorrect}</span>
                      </td>
                      <td className="py-3 px-6 text-sm text-center border-r border-[var(--q3-stroke-normal)]">
                        <span className="text-[var(--q3-neutral-light)] font-bold">{item.totalUnattempted}</span>
                      </td>
                      <td className="py-3 px-6 text-sm text-center border-r border-[var(--q3-stroke-normal)]">
                        <span className="text-[var(--q3-success-normal)] font-bold">{item.totalCorrect}</span>
                      </td>
                      <td className="py-3 px-6 text-sm text-center">
                        <span className="text-[var(--q3-neutral-default)] font-bold">{item.totalQuestions}</span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-[var(--q3-neutral-light)]">
                    No data available for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <Pagination className="mt-6 px-6 pb-4">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(currentPage - 1);
                  }}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              {paginationRange.map((pageNumber, index) => {
                if (typeof pageNumber === 'string') {
                  return (
                    <PaginationItem key={`ellipsis-${index}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                return (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      href="#"
                      isActive={pageNumber === currentPage}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(pageNumber);
                      }}
                      className="cursor-pointer"
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(currentPage + 1);
                  }}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </CardContent>
    </Card>
  );
}
